package dev.kuml.portal.web.editor

import kotlinx.serialization.Serializable

/**
 * Client-side mirror of `kuml-portal-server`'s `dev.kuml.portal.server.diagrams.DiagramOutlineResponse`
 * DTOs (`GET /api/diagrams/{diagramId}/outline`, R-27) -- hand-mirrored, not a shared KMP module
 * (this module's `build.gradle.kts` deliberately excludes a `commonMain` split, same convention
 * `CollabFrame.kt`/`ModelOpCodec.kt` already follow for the server's `CollabWireProtocol.kt`/
 * `ModelOp.kt`). Field names/shapes must be kept in lockstep with the server DTOs by hand.
 */
@Serializable
public data class DiagramOutlineResponse(
    val editVersion: Long = 0,
    val elements: List<OutlineElement>,
)

@Serializable
public data class OutlineElement(
    val id: String,
    val kind: String,
    val name: String,
    val isAbstract: Boolean = false,
    val visibility: String? = null,
    val attributes: List<OutlineFeature> = emptyList(),
    val operations: List<OutlineFeature> = emptyList(),
    val ends: List<OutlineEnd> = emptyList(),
    val sourceId: String? = null,
    val targetId: String? = null,
)

@Serializable
public data class OutlineFeature(
    val id: String,
    val name: String,
    val typeName: String? = null,
    val visibility: String,
    val defaultValue: String? = null,
    val isStatic: Boolean = false,
    val isReadOnly: Boolean = false,
    val isAbstract: Boolean = false,
)

@Serializable
public data class OutlineEnd(
    val typeId: String,
    val role: String? = null,
    val multLower: Int,
    val multUpper: Int? = null,
    val navigable: Boolean,
)

/** Classifier kinds selectable as a diagram NODE (as opposed to a relationship) -- see
 * `DiagramCanvas.kt`'s hit-testing, which only ever needs to validate a `data-kuml-id` against this
 * set before treating a click as a node click. */
private val NODE_KINDS = setOf("class", "interface", "enumeration")

/** Every node (classifier) id in [outline] -- the set `DiagramCanvas.kt`'s hit-testing validates a
 * clicked `g[data-kuml-id]` against BEFORE ever using it (see that file's KDoc "always validate
 * against the outline").
 *
 * **Not** what [dev.kuml.portal.web.editor.DiagramSelection.restore] filters a selection through
 * (review fix, MINOR, 2026-09-02 -- an earlier revision of this KDoc claimed it was, contradicting
 * [allIdsOf]'s KDoc, which makes the same claim for itself). [DiagramSelection.restore] filters
 * BOTH [SelectionState.nodeIds] AND [SelectionState.edgeId] against the SAME `knownIds` set it is
 * given; passing this node-only set there would make every edge selection look stale and clear on
 * the very next refresh, since no edge id is ever a member of this set. [allIdsOf] is the set that
 * call site actually needs. */
public fun nodeIdsOf(outline: DiagramOutlineResponse): Set<String> =
    outline.elements.filter { it.kind in NODE_KINDS }.mapTo(mutableSetOf()) { it.id }

/** Every relationship (non-node) element id in [outline]. */
public fun relationshipIdsOf(outline: DiagramOutlineResponse): Set<String> =
    outline.elements.filterNot { it.kind in NODE_KINDS }.mapTo(mutableSetOf()) { it.id }

/** All ids known to [outline] -- nodes and relationships alike -- the full set
 * `DiagramSelection.restore` filters a stale selection through. */
public fun allIdsOf(outline: DiagramOutlineResponse): Set<String> = outline.elements.mapTo(mutableSetOf()) { it.id }

/** The element with this [id], or `null` if it no longer exists in [outline]. */
public fun elementById(
    outline: DiagramOutlineResponse,
    id: String,
): OutlineElement? = outline.elements.firstOrNull { it.id == id }

/** Every association whose [OutlineElement.ends] touches [nodeId] -- the "Beziehungen" list in a
 * classifier's inspector panel (clicking an entry there selects that edge -- there is no SVG id to
 * click on directly, see K-3/K-4 of the R-27 implementation plan). Generalization/realization/
 * dependency edges touching [nodeId] via [OutlineElement.sourceId]/[OutlineElement.targetId] are
 * included too, so the relationship list is complete regardless of edge kind. */
public fun relationsTouching(
    outline: DiagramOutlineResponse,
    nodeId: String,
): List<OutlineElement> =
    outline.elements.filter { element ->
        when (element.kind) {
            "association" -> element.ends.any { it.typeId == nodeId }
            "generalization", "realization", "dependency" -> element.sourceId == nodeId || element.targetId == nodeId
            else -> false
        }
    }
